import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Lock } from 'lucide-react';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { useExamInsights } from '../context';

interface InsightsUnlockBannerProps {
  /** Analyses left in the learner's running plan; 0 without a plan. */
  analysesRemaining: number;
  analysisLimit: number;
  /** Writing/Speaking went past the free monthly AI grading: the unlock grades it too. */
  aiGradingPending?: boolean;
  className?: string;
}

/** Above a locked IELTS/SAT review: the score is free, this is what the plan adds and how to get it. */
export function InsightsUnlockBanner({ analysesRemaining, analysisLimit, aiGradingPending = false, className }: InsightsUnlockBannerProps) {
  const { t } = useTranslation();
  const { locked, requestUnlock, unlocking, failed } = useExamInsights();
  if (!locked) return null;
  const canSpend = analysesRemaining > 0;

  return (
    <section
      aria-label={t('exam_insights.banner_title')}
      className={cn(
        'my-4 flex flex-col gap-4 rounded-2xl bg-gray-900 p-5 text-white sm:flex-row sm:items-center sm:justify-between sm:p-6',
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Lock className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="font-display text-lg font-bold leading-tight">{t('exam_insights.banner_title')}</p>
          <p className="mt-1 text-sm leading-relaxed text-gray-300">{t('exam_insights.banner_body')}</p>
          {aiGradingPending && <p className="mt-2 text-sm font-medium leading-relaxed text-primary">{t('exam_insights.grading_pending')}</p>}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-start gap-1.5 sm:items-end">
        <Button type="button" onClick={requestUnlock} disabled={unlocking}>
          {unlocking && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
          {canSpend ? t('exam_insights.unlock_now') : t('exam_insights.see_plans')}
        </Button>
        <p className="text-xs text-gray-400">
          {canSpend
            ? t('exam_insights.credits_left', { left: analysesRemaining, total: analysisLimit })
            : t('exam_insights.plans_hint')}
        </p>
        {failed && (
          <p role="alert" className="text-xs text-red-300">
            {t('exam_insights.unlock_failed')}
          </p>
        )}
      </div>
    </section>
  );
}
